import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api, formatApiErrorDetail, setTokens, clearTokens, getAccessToken, setMediaToken } from "@/lib/api";

const AuthContext = createContext(null);

async function fetchMediaToken() {
  try {
    const { data } = await api.get("/auth/media-token");
    setMediaToken(data.media_token);
  } catch (err) {
    console.error("Media token fetch failed (non-blocking):", err);
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, obj = user

  const checkSession = useCallback(async () => {
    if (!getAccessToken()) {
      setUser(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      fetchMediaToken();
    } catch (err) {
      console.error("Session check failed:", err);
      clearTokens();
      setUser(false);
    }
  }, []);

  useEffect(() => {
    checkSession();
  }, [checkSession]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setTokens(data.access_token, data.refresh_token);
      setUser({ id: data.id, email: data.email, name: data.name, role: data.role });
      fetchMediaToken();
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      console.error("Logout request failed (clearing session anyway):", err);
    }
    clearTokens();
    setUser(false);
  };

  const value = useMemo(() => ({ user, login, logout }), [user]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
